import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Text,
  Drawer,
  Select,
  Grid,
  Flex,
  ActionIcon,
  createStyles,
  Alert,
} from "@mantine/core";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import moment from "moment-timezone";
import { useForm } from "@mantine/form";
import { useSelector } from "react-redux";
import { IconPlus, IconTrash, IconAlertCircle } from "@tabler/icons-react";
import {
  getAllAttendenceStaff,
  getAllStaff,
} from "../../services/staff";
import { getAllPatientsModal } from "../../services/patient";
import { getAllFacilitiesModal } from "../../services/facility";
import { addAttendanceStaff } from "../../services/UserTracking";

const useStyles = createStyles(() => ({
  section: {
    padding: "1rem",
    background: "#fff",
    borderRadius: "1rem",
  },
  sectionTitle: {
    fontFamily: "Inter",
    fontSize: "15px",
    fontWeight: 600,
    color: "#1D2939",
    marginTop: "1.5rem",
    marginBottom: "0.75rem",
  },
  fieldLabel: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    color: "#000",
    marginBottom: "4px",
  },
  inputBox: {
    width: "100%",
    padding: "2px 14px",
    borderRadius: "8px",
    height: "44px",
    border: "1px solid #E5E5E5",
    display: "flex",
    alignItems: "center",
    "& input": {
      backgroundColor: "transparent",
      border: "none",
      paddingInline: 0,
      outline: "none",
      width: "100%",
      fontFamily: "Inter",
      fontSize: "14px",
    },
  },
  breakCard: {
    background: "#F5F5F5",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "10px",
  },
}));

const AddAttendanceModal = ({ opened, onClose, staffId, currentPage, range }) => {
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);

  const allStaff = useSelector((state) => state.staff?.allStaff) || [];
  const allPatients = useSelector((state) => state.patient?.allPatients) || [];
  const facilityState = useSelector((state) => state.facility);
  const allFacilities =
    facilityState?.allFacilities?.facilities ||
    facilityState?.allDeletedFacilities?.facilities ||
    [];

  // Load dropdown data if not already in Redux
  useEffect(() => {
    if (!allStaff.length) getAllStaff("all", false, undefined, "");
    if (!allPatients.length) getAllPatientsModal("all", false);
    if (!allFacilities.length) getAllFacilitiesModal("all", false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const form = useForm({
    initialValues: {
      staffId: staffId !== "all" ? staffId : "",
      facilityId: "",
      clientId: "",
      service: "",
      date: new Date(),
      startTime: "",
      endTime: "",
      breaks: [],
    },
  });

  // Reset form every time modal opens
  useEffect(() => {
    if (opened) {
      form.reset();
      if (staffId !== "all") {
        form.setFieldValue("staffId", staffId);
      }
    }
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Derive staff-aware facility logic ───────────────────────────────────

  // The currently active staff ID (from URL param or user selection)
  const currentStaffId = staffId !== "all" ? staffId : form.values.staffId;

  // Find the selected staff object in Redux
  const selectedStaffObj = allStaff?.find(
    (s) => (s._id || s.id) === currentStaffId
  );

  // Normalize the staff's facilityId array to plain string IDs
  const staffFacilityIds = (selectedStaffObj?.facilityId || []).map((fRef) =>
    typeof fRef === "string" ? fRef : fRef?.id || fRef?._id
  );

  // Only the facilities this staff member is actually assigned to
  const staffFacilities = allFacilities?.filter(
    (f) => staffFacilityIds.includes(f?.id || f?._id) && f?.deleted === false
  );

  // Unique checkInTypes among those facilities
  const checkInTypes = new Set(
    staffFacilities?.map((f) => f?.checkInType).filter(Boolean)
  );

  const hasFacilityType = checkInTypes.has("facility");
  const hasClientType = checkInTypes.has("client");
  // Disabled only when a staff is chosen but every facility is "no-clockin"
  const isNoClockIn = Boolean(currentStaffId) && !hasFacilityType && !hasClientType;

  // ─── Filtered options ─────────────────────────────────────────────────────

  const staffOptions = allStaff
    ?.filter((s) => {
      if (s?.active !== true || s?.deleted !== false) return false;
      // Only include staff who have at least one facility with clock-in enabled
      const sFacIds = (s?.facilityId || []).map((fRef) =>
        typeof fRef === "string" ? fRef : fRef?.id || fRef?._id
      );
      return allFacilities?.some(
        (f) =>
          sFacIds.includes(f?.id || f?._id) &&
          f?.deleted === false &&
          (f?.checkInType === "facility" || f?.checkInType === "client")
      );
    })
    ?.map((s) => ({
      value: s._id || s.id,
      label:
        `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.name || s._id,
    }));

  // Facility dropdown: only "facility" checkInType from this staff's list
  const facilityOptions = staffFacilities
    ?.filter((f) => f?.checkInType === "facility" && f?.active === true)
    ?.map((f) => ({ value: f?.id || f?._id, label: f?.name }));

  // IDs of "client" type facilities for this staff (used to filter clients)
  const clientTypeFacilityIds = staffFacilities
    ?.filter((f) => f?.checkInType === "client" && f?.active === true)
    ?.map((f) => f?.id || f?._id);

  // Client dropdown: only clients who belong to a "client"-type facility of this staff
  const clientOptions = allPatients
    ?.filter((p) => {
      if (p?.deleted !== false || p?.active !== true) return false;
      return p?.facilityId?.some((fRef) => {
        const fId = typeof fRef === "string" ? fRef : fRef?.id || fRef?._id;
        return clientTypeFacilityIds?.includes(fId);
      });
    })
    ?.map((p) => ({
      value: p?.id || p?._id,
      label:
        p?.name ||
        `${p?.form1?.firstName || ""} ${p?.form1?.lastName || ""}`.trim(),
    }));

  const selectedClientObj = allPatients?.find(
    (p) => (p?.id || p?._id) === form.values.clientId
  );
  const serviceOptions =
    selectedClientObj?.form1?.services?.map((ser) => ({
      label: ser?.servicetitle,
      value: ser?.servicenumber,
    })) || [];

  // ─── Break helpers ────────────────────────────────────────────────────────

  const addBreak = () => {
    form.insertListItem("breaks", { breakStart: "", breakEnd: "" });
  };

  const removeBreak = (index) => {
    form.removeListItem("breaks", index);
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async (values) => {
    const { staffId: sId, facilityId, clientId, service, date, startTime, endTime, breaks } = values;

    // Manual validation
    let hasError = false;
    if (!sId) { form.setFieldError("staffId", "Staff is required"); hasError = true; }
    if (hasFacilityType && !clientId && !facilityId) { form.setFieldError("facilityId", "Facility is required"); hasError = true; }
    if (hasClientType && !facilityId && !clientId) { form.setFieldError("clientId", "Client is required"); hasError = true; }
    if (!date) { form.setFieldError("date", "Date is required"); hasError = true; }
    if (!startTime) { form.setFieldError("startTime", "Clock-in time is required"); hasError = true; }
    if (!endTime) { form.setFieldError("endTime", "Clock-out time is required"); hasError = true; }
    if (hasError) return;

    try {
      setLoading(true);

      // Resolve the facility timezone to use for all time construction.
      // For facility-type check-in use the selected facility; for client-type
      // use the first client-type facility assigned to this staff member.
      const facLookupId = facilityId || clientTypeFacilityIds?.[0] || null;
      const facObj = staffFacilities?.find((f) => (f?.id || f?._id) === facLookupId);
      const facilityTz = facObj?.timeZone || null;

      const dateStr = moment(date).format("YYYY-MM-DD");

      // Build a Date from a "HH:mm" string anchored to dateStr in facilityTz.
      // Falls back to browser-local construction when no timezone is known.
      const buildDate = (timeStr) => {
        if (facilityTz) {
          return moment.tz(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm", facilityTz).toDate();
        }
        const d = new Date(date);
        const [h, m] = timeStr.split(":").map(Number);
        d.setHours(h, m, 0, 0);
        return d;
      };

      const _date = facilityTz
        ? moment.tz(dateStr, "YYYY-MM-DD", facilityTz).startOf("day").toDate()
        : (() => { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; })();

      const _startTime = buildDate(startTime);
      const _endTime = buildDate(endTime);

      if (_endTime <= _startTime) {
        form.setFieldError("endTime", "Clock-out must be after clock-in");
        return;
      }

      const breakObjects = breaks
        .filter((b) => b.breakStart && b.breakEnd)
        .map((b) => ({
          breakStart: buildDate(b.breakStart),
          breakEnd: buildDate(b.breakEnd),
        }));

      const payload = {
        staff: sId,
        facility: facilityId || null,
        checkin_type: clientId ? "client" : "facility",
        date: _date,
        startTime: _startTime,
        endTime: _endTime,
        status: "clock-out",
        breaks: breakObjects,
      };

      if (clientId) {
        payload.client = clientId;
        payload.service = service;
      }

      const result = await addAttendanceStaff(payload);
      if (result) {
        await getAllAttendenceStaff(currentPage, range);
        onClose();
      }
    } catch (e) {
      console.error("AddAttendanceModal error:", e);
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Drawer
      size="70%"
      shadow="md"
      radius={15}
      zIndex={1000}
      opened={opened}
      onClose={onClose}
      overlayProps={{ blur: 3 }}
      position="right"
      title={
        <Text ff="Inter" fz={22} fw={600}>
          Add Attendance
        </Text>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(form.values); }}>
        <Box className={classes.section}>

          {/* ── Staff & Assignment ── */}
          <Text className={classes.sectionTitle}>Staff & Assignment</Text>
          <Grid gutter={12}>

            {/* Staff — only shown on the "all" page */}
            {staffId === "all" && (
              <Grid.Col span={12}>
                <Select
                  label="Staff"
                  placeholder="Select staff"
                  data={staffOptions}
                  value={form.values.staffId}
                  onChange={(val) => {
                    form.setFieldValue("staffId", val);
                    // Reset assignment fields when staff changes
                    form.setFieldValue("facilityId", "");
                    form.setFieldValue("clientId", "");
                    form.setFieldValue("service", "");
                  }}
                  error={form.errors.staffId}
                  searchable
                  required
                />
              </Grid.Col>
            )}

            {/* No-clockin warning */}
            {isNoClockIn && (
              <Grid.Col span={12}>
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="orange"
                  variant="light"
                >
                  Clock-in is disabled for this staff member's facility. Time logs aren't available.
                </Alert>
              </Grid.Col>
            )}

            {/* Facility dropdown — only when staff has "facility"-type facilities */}
            {hasFacilityType && (
              <Grid.Col span={hasClientType ? 6 : 12}>
                <Select
                  label="Facility"
                  placeholder="Select facility"
                  data={facilityOptions}
                  value={form.values.facilityId}
                  onChange={(val) => {
                    form.setFieldValue("facilityId", val);
                    form.setFieldValue("clientId", "");
                    form.setFieldValue("service", "");
                  }}
                  error={form.errors.facilityId}
                  searchable
                  required={!form.values.clientId}
                />
              </Grid.Col>
            )}

            {/* Client dropdown — only when staff has "client"-type facilities */}
            {hasClientType && (
              <Grid.Col span={hasFacilityType ? 6 : 12}>
                <Select
                  label={hasFacilityType ? "Client (Optional)" : "Client"}
                  placeholder="Select client"
                  data={clientOptions}
                  value={form.values.clientId}
                  onChange={(val) => {
                    form.setFieldValue("clientId", val);
                    form.setFieldValue("service", "");
                    // If client is selected, clear facility since types are exclusive
                    if (!hasFacilityType) form.setFieldValue("facilityId", "");
                  }}
                  error={form.errors.clientId}
                  searchable
                  clearable={hasFacilityType}
                  required={!hasFacilityType}
                />
              </Grid.Col>
            )}

            {/* Service — only when a client is selected */}
            {form.values.clientId && (
              <Grid.Col span={12}>
                <Select
                  label="Service"
                  placeholder="Select service"
                  data={serviceOptions}
                  value={form.values.service}
                  onChange={(val) => form.setFieldValue("service", val)}
                  clearable
                />
              </Grid.Col>
            )}
          </Grid>

          {/* ── Date & Time ── */}
          <Text className={classes.sectionTitle}>Date & Time</Text>
          <Grid gutter={12}>
            <Grid.Col span={12}>
              <DatePickerInput
                label="Date"
                placeholder="Select date"
                value={form.values.date}
                onChange={(val) => form.setFieldValue("date", val)}
                error={form.errors.date}
                maxDate={new Date()}
                required
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <Box>
                <Text className={classes.fieldLabel}>
                  Clock-in Time <span style={{ color: "red" }}>*</span>
                </Text>
                <Box className={classes.inputBox}>
                  <TimeInput
                    value={form.values.startTime}
                    onChange={(e) => form.setFieldValue("startTime", e.target.value)}
                    styles={{ input: { border: "none", background: "transparent", padding: 0 } }}
                  />
                </Box>
                {form.errors.startTime && (
                  <Text c="red" fz="xs" mt={4}>{form.errors.startTime}</Text>
                )}
              </Box>
            </Grid.Col>

            <Grid.Col span={6}>
              <Box>
                <Text className={classes.fieldLabel}>
                  Clock-out Time <span style={{ color: "red" }}>*</span>
                </Text>
                <Box className={classes.inputBox}>
                  <TimeInput
                    value={form.values.endTime}
                    onChange={(e) => form.setFieldValue("endTime", e.target.value)}
                    styles={{ input: { border: "none", background: "transparent", padding: 0 } }}
                  />
                </Box>
                {form.errors.endTime && (
                  <Text c="red" fz="xs" mt={4}>{form.errors.endTime}</Text>
                )}
              </Box>
            </Grid.Col>
          </Grid>

          {/* ── Break Times ── */}
          <Flex align="center" justify="space-between" mt="1.5rem" mb="0.75rem">
            <Text className={classes.sectionTitle} style={{ margin: 0 }}>
              Break Times
            </Text>
            <Button
              size="xs"
              variant="light"
              color="yellow"
              c="#000"
              leftIcon={<IconPlus size={14} />}
              onClick={addBreak}
              type="button"
            >
              Add Break
            </Button>
          </Flex>

          {form.values.breaks.map((_, index) => (
            <Box key={index} className={classes.breakCard}>
              <Flex justify="space-between" align="center" mb={8}>
                <Text fz={13} fw={600} color="#344054">
                  Break {index + 1}
                </Text>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  size="sm"
                  onClick={() => removeBreak(index)}
                  type="button"
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Flex>

              <Grid gutter={12}>
                <Grid.Col span={6}>
                  <Box>
                    <Text className={classes.fieldLabel}>Break Start</Text>
                    <Box className={classes.inputBox}>
                      <TimeInput
                        value={form.values.breaks[index]?.breakStart || ""}
                        onChange={(e) =>
                          form.setFieldValue(`breaks.${index}.breakStart`, e.target.value)
                        }
                        styles={{ input: { border: "none", background: "transparent", padding: 0 } }}
                      />
                    </Box>
                  </Box>
                </Grid.Col>

                <Grid.Col span={6}>
                  <Box>
                    <Text className={classes.fieldLabel}>Break End</Text>
                    <Box className={classes.inputBox}>
                      <TimeInput
                        value={form.values.breaks[index]?.breakEnd || ""}
                        onChange={(e) =>
                          form.setFieldValue(`breaks.${index}.breakEnd`, e.target.value)
                        }
                        styles={{ input: { border: "none", background: "transparent", padding: 0 } }}
                      />
                    </Box>
                  </Box>
                </Grid.Col>
              </Grid>
            </Box>
          ))}

          {/* ── Actions ── */}
          <Flex justify="flex-end" gap={10} mt="2rem">
            <Button variant="subtle" color="gray" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              type="submit"
              color="yellow"
              c="#000"
              loading={loading}
              disabled={isNoClockIn}
            >
              Add Attendance
            </Button>
          </Flex>
        </Box>
      </form>
    </Drawer>
  );
};

export default AddAttendanceModal;
